import {faFolder} from "@fortawesome/pro-solid-svg-icons/faFolder";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {MailItemType} from "@twigsby/client-data";
import {MailFile} from "@twigsby/client-data/lib/Types/MailFileTypes";
import {
	Badge,
	ColourOutline,
	ColourSolid,
	ItemRow,
	ItemRowSize,
	Pill,
	StackVertical,
	useLayout
} from "@twigsby/frontend-framework";
import {CustomCheckbox} from "@twigsby/frontend-framework/lib/Components/Forms/Toggle/ToggleSwitchCustom";
import {FormatLocaleDateTime} from "@twigsby/helpers";
import React, {memo} from 'react';
import {useMailbox} from "../../Providers/NewMailboxProvider";
import {useSite} from "../../Providers/SiteInterfaceProvider";
import {MailItemThumbnails, MailPartTypeEnum} from './MailItemThumbnails';
import {MailTagCollection} from "./MailTagCollection";

interface MailItemMinimalComponentType extends MailItemType {
	selected? : boolean;
	selectable? : boolean;
	index? : string;
	callBack?: any;
	disabled?: boolean;
	forwardingPreview?: boolean;
}

type MailITemThumbnailsType = {
	files: Array<MailFile>;
	onClickCallback: (link?: string) => void;
}

const MailItemThumbs = (props: MailITemThumbnailsType) => {
	return (
		<>
			<MailItemThumbnails mailType={MailPartTypeEnum['Mail-Envelopes']} files={props.files} onClickCallback={(link: string) => props.onClickCallback(link)} />
			<MailItemThumbnails mailType={MailPartTypeEnum['Mail-Contents']} files={props.files} onClickCallback={(link: string) => props.onClickCallback(link)} />
		</>
	)
}

const MailItemMinimal = memo((props: MailItemMinimalComponentType) => {
	const { mailBoxInfo, } = useMailbox();

	const { setPdfViewer } = useSite();
	const { isSmall, isLarge, isXL } = useLayout();

	const TopUpRequired = props.files[0]?.topupRequired || props.files[1]?.topupRequired;

	return (
		<ItemRow
			muted={props.dateRead !== null}
			highlighted={!props.dateRead}
			selected={props.selected}
			static={!props.callBack}
			size={ItemRowSize.Large}
			disabled={props.disabled}
			extraProps={{
				'data-item-id': props.mailUuid,
				'onClick': (e: any) => props.callBack && props.callBack(props.mailUuid)
			}}
			extraClass={props.forwardingPreview ? 'mail-item-forward-preview' : ''}
		>
			<div className={`item-row-checkbox`}>
				{props.selectable && (
					<CustomCheckbox
						onChange={(e) => null}
						label={props.mailUuid}
						name={`mailItem${props.mailUuid}`}
						hideLabel={true}
						defaultChecked={props.selected}
					/>
				)}
			</div>

			<div className={'mailbox-item-meta mailbox-minimal'} draggable={false}>
				{!isSmall && (
					<div className={props.files.length > 1 ? `thumbnail-stack` : ''}>
						<MailItemThumbs
							files={props.files}
							onClickCallback={(link) => setPdfViewer(true, link)}
						/>
					</div>
				)}

				{isSmall && (
					<MailItemThumbs
						files={props.files}
						onClickCallback={(link) => setPdfViewer(true, link)}
					/>
				)}

				<div className='flex-row flex-gap-sm mail-item-reference'>
					{!props.dateRead && <Badge colour={ColourSolid.Primary} />}
					<StackVertical>
						<span className='mail-item-tracking-id'>
							{props.trackingId}
						</span>
						{props.clientFolder?.parentFolder && mailBoxInfo.customFolderId !== props.clientFolder?.id && (
							<div className={`pill flex-gap-xs ${props.clientFolder?.colour ? `${props.clientFolder?.colour}-solid` : ColourSolid.Secondary}`}>
								<FontAwesomeIcon icon={faFolder} fixedWidth={true} />
								{props.clientFolder?.description}
							</div>
						)}
						{props.daysUntilNextStorageCharge && (
							<Pill content={'Storage Charge in 14 days'} colour={ColourOutline.Warning} />
						)}
						{TopUpRequired && (
							<Pill content={'Top-up Required'} colour={ColourOutline.Alert} />
						)}
						{!props.daysUntilNextStorageCharge && !TopUpRequired && !props.dateRead && <small>{FormatLocaleDateTime(new Date(props.dateReceived))}</small>}
					</StackVertical>
				</div>

				{isLarge && (
					<div className='mail-item-tags item-row-tags'>
						{props.tags && (
							<MailTagCollection tags={props.tags} small={true} displayLimit={3}  linkSlug={'/mail/tagged/'} />
						)}
					</div>
				)}
				{isXL && (
					<div className='mail-item-notes'>
						<div className={'notes-content-wrapper'}>{props.note}</div>
					</div>
				)}
			</div>
		</ItemRow>
	);
});

export default MailItemMinimal;
